import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, extractError } from "../lib/api";
import { isDemoActive } from "../demo/demoInterceptor";

const AuthContext = createContext(null);

// Iter 20.08 — Bei aktivem Demo-Modus liefern wir einen synthetischen
// „Demo-User" statt echten /auth/me-Call. So laufen alle Pages, die
// useAuth() abfragen (UserLayout etc.), auch ohne echten Login.
const DEMO_USER = {
  id: "demo-user",
  email: "demo@einstufungstool.at",
  name: "Demo-Benutzer",
  role: "user",
  status: "aktiv",
  company_id: null,
};

export function AuthProvider({ children }) {
  // user states: undefined = checking, null = not authenticated, object = authenticated
  const [user, setUser] = useState(undefined);

  const fetchMe = useCallback(async () => {
    // Iter 20.08 — Demo-Modus umgeht /auth/me komplett.
    if (isDemoActive()) {
      setUser(DEMO_USER);
      return DEMO_USER;
    }
    // Iter 19.41: Explicit 12s timeout + single retry so a backend that is
    // busy serving a long-running upload (e.g. scanned PDF + Claude Vision)
    // does not freeze the AuthProvider in `user === undefined` forever and
    // leave the user staring at the "Wird geladen…" splash.
    const attempt = async () => {
      const { data } = await api.get("/auth/me", { timeout: 12_000 });
      return data;
    };
    try {
      const data = await attempt();
      setUser(data);
      return data;
    } catch (err1) {
      // Network/timeout? Retry once after 1.2s — gives the backend time to
      // finish the in-flight upload and start serving normally again.
      const code1 = err1?.code;
      const status1 = err1?.response?.status;
      const transient =
        code1 === "ECONNABORTED" ||
        code1 === "ETIMEDOUT" ||
        code1 === "ERR_NETWORK" ||
        status1 === 502 ||
        status1 === 503 ||
        status1 === 504;
      if (!transient) {
        // Real auth failure (401) → user is not logged in.
        setUser(null);
        return null;
      }
      await new Promise((r) => setTimeout(r, 1200));
      try {
        const data = await attempt();
        setUser(data);
        return data;
      } catch {
        // Still failing — treat as logged-out so the UI escapes the splash
        // and shows the public landing/login. The user can retry manually.
        setUser(null);
        return null;
      }
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true, user: data };
    } catch (err) {
      return {
        ok: false,
        error: extractError(err, "Anmeldung fehlgeschlagen."),
        code: err?.response?.data?.detail?.code || null,
      };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      return { ok: true, user: data };
    } catch (err) {
      return { ok: false, error: extractError(err, "Registrierung fehlgeschlagen.") };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(null);
  };

  const value = { user, setUser, login, register, logout, refresh: fetchMe };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
